import { assign, createMachine, MachineConfig } from 'xstate';
import { getDatabase } from 'firebase/database';
import creditCardStates from './creditCard';
import checkoutStates from './checkout';
import selectPaymentTypeStates, { PaymentType } from './selectPaymentType';
import caseTypeStates, { CaseType } from './caseType';
import { SubmitCaseContext, SubmitCaseEvent } from './types';
import * as guards from './guards';
import * as actions from './actions';
import { saveDraftWithProgress } from './actions';
import { AllowedSpecies, Sex } from '../../services/buildfire/rdb/cases';
import { getDraftById, getDraft } from '../../services/firebase/drafts';
import { SUBMIT_CASE_DRAFT_ID } from '../../constants/localStorage';

const config: MachineConfig<SubmitCaseContext, any, SubmitCaseEvent> = {
  id: 'Submit Case Form',
  initial: 'load',
  context: {
    paymentType: PaymentType.CREDIT_CARD,
    stripePublishableKey: '',
    creditCards: [],
    userId: '',
    email: '',
    name: '',
    caseType: CaseType.STANDARD,
    caseInfo: {
      // submitterFirstName: 'Jeremy',
      // submitterLastName: 'Berglund',
      // patientFirstName: 'Test',
      // patientLastName: 'Cat',
      // age: 1,
      // sex: Sex.MALE,
      // species: AllowedSpecies.CAT,
      // fixed: true,
      // description: 'Some info here',
      // sites: [
      //   {
      //     location: 'Back',
      //     description: 'Test',
      //     images: ['case-attachment-1725555053024-1-1.jpeg'],
      //   },
      // ],
    },
  } as any,
  on: {
    PARENT_ACCOUNT_CHANGE: {
      actions: 'updateParentAccount',
    },
  },
  states: {
    load: {
      invoke: {
        src: 'loadDraft',
        onDone: [
          {
            target: 'checkout',
            cond: (context, event) =>
              context.targetPage === 'checkout' &&
              guards.hasDraft(context, event) &&
              guards.canAutoLoadToCheckout(context, event),
            actions: ['storeDraft', 'setDraftData'],
          },
          {
            target: 'creditCard',
            cond: (context, event) =>
              context.targetPage === 'payment' &&
              guards.hasDraft(context, event) &&
              guards.canAutoLoadToPayment(context, event),
            actions: ['storeDraft', 'setDraftData'],
          },
          {
            target: 'selectPaymentType',
            cond: (context, event) =>
              context.targetPage === 'selectPaymentType' &&
              guards.hasDraft(context, event) &&
              guards.canAutoLoadToSelectPaymentType(context, event),
            actions: ['storeDraft', 'setDraftData'],
          },
          {
            target: 'enterInfo',
            cond: (context, event) =>
              context.targetPage === 'enterInfo' &&
              guards.hasDraft(context, event) &&
              guards.canAutoLoadToEnterInfo(context, event),
            actions: ['storeDraft', 'setDraftData'],
          },
          {
            target: 'draftLoadConfirmation',
            cond: 'hasDraft',
            actions: 'storeDraft',
          },
          {
            target: 'caseType',
            actions: 'handleDraftLoadError',
          },
        ],
        onError: {
          target: 'caseType',
          actions: 'handleDraftLoadError',
        },
      },
    },
    draftLoadConfirmation: {
      on: {
        LOAD_DRAFT: [
          {
            target: 'checkout',
            cond: (context) =>
              context.targetPage === 'checkout' &&
              guards.canAutoLoadToCheckout(context, {
                data: context.pendingDraft,
              }),
            actions: 'setDraftData',
          },
          {
            target: 'creditCard',
            cond: (context) =>
              context.targetPage === 'payment' &&
              guards.canAutoLoadToPayment(context, {
                data: context.pendingDraft,
              }),
            actions: 'setDraftData',
          },
          {
            target: 'selectPaymentType',
            cond: (context) =>
              context.targetPage === 'selectPaymentType' &&
              guards.canAutoLoadToSelectPaymentType(context, {
                data: context.pendingDraft,
              }),
            actions: 'setDraftData',
          },
          {
            target: 'enterInfo',
            cond: (context) =>
              context.targetPage === 'enterInfo' &&
              guards.canAutoLoadToEnterInfo(context, {
                data: context.pendingDraft,
              }),
            actions: 'setDraftData',
          },
          {
            target: 'caseType',
            actions: 'setDraftData',
          },
        ],
        CLEAR_DRAFT: {
          target: 'caseType',
          actions: 'clearDraftData',
        },
      },
    },
    caseType: {
      ...(caseTypeStates as any),
      entry: 'updateUrlToCaseType',
      on: {
        NEXT: 'enterInfo',
      },
    },
    enterInfo: {
      entry: 'updateUrlToEnterInfo',
      on: {
        NEXT: [
          {
            target: 'selectPaymentType',
            cond: {
              type: 'every',
              guards: ['isCaseInfoValid', 'doesNotNeedUpload'],
            },
            actions: ['setCaseInfo', 'saveDraftQuick'],
          },
          {
            target: 'savingDraft',
            cond: 'isCaseInfoValid',
            actions: 'setCaseInfo',
          },
        ],
        BACK: 'caseType',
      },
    },
    savingDraft: {
      on: {
        SET_UPLOAD_PROGRESS: {
          actions: 'setUploadProgress',
        },
        SET_UPLOADING_DRAFT: {
          actions: 'setUploadingDraft',
        },
        SET_CASE_INFO: {
          actions: 'setCaseInfo',
        },
      },
      invoke: {
        src: 'saveDraft',
        onDone: [
          {
            target: 'enterInfo',
            cond: (_context, event) => {
              const caseInfo = event.data;
              return caseInfo.sites.some((site: any) =>
                site.images.some(
                  (img: any) =>
                    typeof img === 'object' &&
                    'status' in img &&
                    img.status === 'failed'
                )
              );
            },
            actions: 'setUploadedCaseInfo',
          },
          {
            target: 'selectPaymentType',
            actions: 'setUploadedCaseInfo',
          },
        ],
        onError: {
          target: 'enterInfo',
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },
    selectPaymentType: {
      ...(selectPaymentTypeStates as any),
      entry: 'updateUrlToSelectPaymentType',
      on: {
        NEXT: 'creditCard',
        BACK: 'enterInfo',
      },
      onDone: 'creditCard',
    },
    creditCard: {
      ...(creditCardStates as any),
      entry: 'updateUrlToPayment',
      onDone: 'checkout',
      on: {
        BACK: [
          {
            target: 'enterInfo',
            cond: 'isInvoicingDisabled',
          },
          {
            target: 'selectPaymentType',
          },
        ],
      },
    },
    checkout: {
      ...(checkoutStates as any),
      entry: 'updateUrlToCheckout',
      onDone: {
        target: 'submitted',
        actions: 'clearDraft',
      },
      on: {
        BACK: 'creditCard',
      },
    },
    error: {},
    submitted: {
      type: 'final',
    },
  },
};

const submitCaseMachine = createMachine(config, {
  actions: {
    ...(actions as any),
    updateUrlToCaseType: actions.updateUrlPage('caseType'),
    updateUrlToEnterInfo: actions.updateUrlPage('enterInfo'),
    updateUrlToSelectPaymentType: actions.updateUrlPage('selectPaymentType'),
    updateUrlToPayment: actions.updateUrlPage('payment'),
    updateUrlToCheckout: actions.updateUrlPage('checkout'),
  },
  services: {
    loadDraft: async (context) => {
      const database = getDatabase(context.firebaseApp);
      const draftId = localStorage.getItem(SUBMIT_CASE_DRAFT_ID);

      if (draftId) {
        // Try to load specific draft
        const draft = await getDraftById(database, context.userId, draftId);
        return draft;
      } else {
        // Try to load latest draft
        const draft = await getDraft(database, context.userId);
        return draft;
      }
    },
    saveDraft: (context, event) => (send) => {
      return saveDraftWithProgress(context, send);
    },
  },
  guards: {
    ...guards,
    every: (ctx, event, { cond }) => {
      if (cond.type !== 'every') {
        throw new Error('Error: "every" condition expected type to be "every"');
      }
      return cond.guards.every((guardKey) => guards[guardKey](ctx, event));
    },
  },
});

export * from './types';

export default submitCaseMachine;
